// --- React & Redux
import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
import Banner from '@dtci/spellbook/Banner';
import ContentBlock from '@dtci/spellbook/ContentBlock';
import Typography from '@dtci/spellbook/Typography';
import Button from '@dtci/spellbook/Button';
// --- Components
import { JobStatusDrawer } from '../status-drawer';
// --- Hooks & Actions
import { useProductGateActions } from 'datastage/store/productGate/useProductGate';
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Utils
import { datastageApi } from 'datastage/api';
import { startJobStatusTracker } from 'datastage/utils/jobStatusTracker';
import { filterCriteria } from 'datastage/utils/filterCriteria';
import { useRLProductGateGridUtils } from './utils/gridUtils';
import { usePermissions } from 'datastage/utils/permissions';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';

/**
 * Rightsline Product Gate Grid
 *
 * Features:
 * - Renders a data grid for Rightsline Product Gate product updates
 *
 * @returns {JSX.Element}
 */
export const RightslineProductGateGrid = () => {
    const agGridApi = useAgGridApi();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();
    const { updateProductGate } = useProductGateActions();
    const showSpinner = useSelector((s) => s.spinner.showSpinner);
    const {
        productUpdates,
        filterForm,
        productGateSort,
        productGateRange,
        espnTitle,
    } = useSelector((s) => s.productGate);
    const { isRLProductGateAdmin, isRLProductGateEspnPlusUser, isRLProductGateUser } =
        usePermissions();
    const [selectedRows, setSelectedRows] = useState([]);
    const [banner, setBanner] = useState(null);

    // Refresh product updates data
    const refreshProductUpdates = useCallback(async () => {
        const data = await datastageApi.rightsLineProductUpdates(
            filterCriteria(filterForm, espnTitle, 'productUpdates'),
            productGateRange,
            productGateSort,
        );

        // Check for error (has code field) or null (network/auth error)
        if (!data || data.code) {
            showErrorToast(
                data?.message || 'An unexpected error occurred while fetching product updates.',
            );
            return false;
        }

        updateProductGate({
            productUpdates: data?.content ?? [],
            totalPages: data?.totalPages ?? 0,
            totalItems: data?.totalElements ?? 0,
            currentPage: data?.pageable?.pageNumber ?? 0,
        });
        return true;
    }, [filterForm, espnTitle, productGateRange, productGateSort, showErrorToast, updateProductGate]);

    // Handle closing the banner and refreshing data
    const handleClose = useCallback(async () => {
        agGridApi.AgGridApiRef.current?.api?.deselectAll?.();
        setBanner(null);
        setSelectedRows([]);

        await refreshProductUpdates();
    }, [agGridApi, refreshProductUpdates]);

    // Clear selections when ESPN toggle changes (data refresh handled by search form)
    useEffect(() => {
        agGridApi.AgGridApiRef.current?.api?.deselectAll?.();
        setBanner(null);
        setSelectedRows([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [espnTitle]);

    const onAddProducts = useCallback(
        async (data) => {
            setLoadingTrue();
            try {
                console.log('Adding products with data:', data);
                const res = await datastageApi.addProducts(data);
                console.log('Adding products with response:', res);

                // Check for error (has code field) or null (network/auth error)
                if (!res || res.code) {
                    showErrorToast(
                        res?.message || 'An unexpected error occurred while adding products.',
                    );
                    return;
                }

                // Success - check for job ID
                if (res?.id || res?.sid) {
                    const jobId = res.id || res.sid;

                    startJobStatusTracker(jobId, (message, status, data, isComplete) => {
                        // Keep drawer open while job is running OR for 3 seconds after completion
                        if (isComplete) {
                            // Job finished - keep drawer open with completion message
                            updateProductGate({
                                isJobStatusOpen: true,
                                jobDetails: data,
                            });

                            // Refresh grid immediately
                            refreshProductUpdates();

                            // Close drawer after 3 seconds
                            setTimeout(() => {
                                updateProductGate({
                                    isJobStatusOpen: false,
                                    jobDetails: null,
                                });
                            }, 3000);
                        } else {
                            // Job still running - keep drawer open
                            const isActiveJob = data?.spaJobStatus === 'STARTING' || data?.spaJobStatus === 'RUNNING';
                            updateProductGate({
                                isJobStatusOpen: isActiveJob,
                                jobDetails: data,
                            });
                        }
                    });

                    handleClose();
                } else {
                    showErrorToast('Job created but no job ID returned from server.');
                }
            } catch (error) {
                showErrorToast(error?.message || 'Unexpected error adding products.');
                console.error('Unexpected error adding products:', error);
            } finally {
                setLoadingFalse();
            }
        },
        [setLoadingTrue, setLoadingFalse, showErrorToast, updateProductGate, handleClose],
    );

    const onChangeIgnoreFlag = useCallback(
        async (product) => {
            setLoadingTrue();
            try {
                const res = product.ignoreFlag
                    ? await datastageApi.unIgnoreProducts(product)
                    : await datastageApi.ignoreProducts(product);

                // Check for error (has code field) or null (network/auth error)
                if (!res || res.code) {
                    showErrorToast(
                        res?.message || 'An unexpected error occurred while updating ignore flag.',
                    );
                    return;
                }

                // Success - refresh the grid
                const refreshSuccess = await refreshProductUpdates();
                if (refreshSuccess) {
                    handleClose();
                }
            } catch (error) {
                showErrorToast(error?.message || 'Unexpected error updating ignore flag.');
                console.error('Unexpected error updating ignore flag:', error);
            } finally {
                setLoadingFalse();
            }
        },
        [setLoadingTrue, setLoadingFalse, showErrorToast, refreshProductUpdates, handleClose],
    );

    // Handle bulk ignore/unignore for banner buttons
    const onBulkChangeIgnoreFlag = useCallback(
        async (products) => {
            setLoadingTrue();
            try {
                // Determine action based on first product's ignore flag
                const shouldUnignore = products[0]?.ignoreFlag;
                const res = shouldUnignore
                    ? await datastageApi.unIgnoreProducts(products)
                    : await datastageApi.ignoreProducts(products);

                // Check for error (has code field) or null (network/auth error)
                if (!res || res.code) {
                    showErrorToast(
                        res?.message || 'An unexpected error occurred while updating ignore flag.',
                    );
                    return;
                }

                // Success - refresh the grid
                const refreshSuccess = await refreshProductUpdates();
                if (refreshSuccess) {
                    handleClose();
                }
            } catch (error) {
                showErrorToast(error?.message || 'Unexpected error updating ignore flag.');
                console.error('Unexpected error updating ignore flag:', error);
            } finally {
                setLoadingFalse();
            }
        },
        [setLoadingTrue, setLoadingFalse, showErrorToast, refreshProductUpdates, handleClose],
    );

    // Grid utilities for sorting and columns
    const { columns } = useRLProductGateGridUtils({
        columnHandlers: [onAddProducts, onChangeIgnoreFlag],
        espnTitle,
    });

    const onUpdateProducts = useCallback(async () => {
        setLoadingTrue();
        try {
            const res = await datastageApi.updateProducts(selectedRows);

            // Check for error (has code field) or null (network/auth error)
            if (!res || res.code) {
                showErrorToast(
                    res?.message || 'An unexpected error occurred while updating products.',
                );
                return;
            }

            // Success - check for job ID
            if (res?.id || res?.sid) {
                const jobId = res.id || res.sid;

                startJobStatusTracker(jobId, (message, status, data, isComplete) => {
                    // Keep drawer open for all job statuses until user closes it
                    updateProductGate({
                        isJobStatusOpen: true,
                        jobDetails: data,
                    });

                    // Refresh grid when job completes
                    if (isComplete) {
                        refreshProductUpdates();
                    }
                });

                handleClose();
            } else {
                showErrorToast('Job created but no job ID returned from server.');
            }
        } catch (error) {
            showErrorToast(
                error?.message || 'Unexpected error updating products.',
            );
            console.error('Unexpected error updating products:', error);
        } finally {
            setLoadingFalse();
        }
    }, [selectedRows, setLoadingTrue, setLoadingFalse, showErrorToast, updateProductGate, handleClose]);

    // Handle selection changes in the data grid
    const onSelectionChanged = useCallback((event) => {
        const selected = event.api.getSelectedRows();
        setSelectedRows(selected);
        if (selected.length > 0) {
            setBanner(`${selected.length} item${selected.length === 1 ? '' : 's'} selected`);
        } else {
            setBanner(null);
        }
    }, []);

    // Handle moving products between ESPN+ and Content Sales
    const handleMoveProducts = useCallback(async () => {
        setLoadingTrue();
        try {
            const res = await datastageApi.moveProducts(selectedRows, espnTitle);

            // Check for error (has code field) or null (network/auth error)
            if (!res || res.code) {
                showErrorToast(
                    res?.message || 'An unexpected error occurred while moving products.',
                );
                return;
            }

            // Success - refresh the grid
            const refreshSuccess = await refreshProductUpdates();
            if (refreshSuccess) {
                handleClose();
            }
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error moving products.');
            console.error('Unexpected error moving products:', error);
        } finally {
            setLoadingFalse();
        }
    }, [selectedRows, espnTitle, setLoadingTrue, setLoadingFalse, showErrorToast, handleClose, refreshProductUpdates]);

    return (
        <>
            {banner && (
                <Banner
                    faPackage="far"
                    iconTypePrimary={'fa-check-circle'}
                    role="status"
                    type={'success'}
                    onClose={handleClose}
                    style={{
                        width: '100%',
                        minHeight: 48,
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 12,
                        padding: '12px 20px',
                        boxSizing: 'border-box',
                    }}
                >
                    <ContentBlock
                        className="banner-content"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 12,
                            width: '100%',
                            minHeight: 48,
                        }}
                    >
                        <Typography className="main-banner-text" variant="T20">
                            {banner}
                        </Typography>
                        <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
                            {(isRLProductGateAdmin || isRLProductGateEspnPlusUser) && (
                                <Button
                                    style={{ color: '#3611c8' }}
                                    iconType="fa-solid fa-share"
                                    type="button"
                                    styleType="link"
                                    onClick={handleMoveProducts}
                                >
                                    {espnTitle ? 'MOVE TO CONTENT SALES' : 'MOVE TO ESPN+'}
                                </Button>
                            )}

                            {(espnTitle ? isRLProductGateAdmin : isRLProductGateUser) && (
                                <>
                                    <Button
                                        style={{ color: '#3611c8' }}
                                        iconType="fa-solid fa-plus"
                                        type="button"
                                        styleType="link"
                                        onClick={() => onAddProducts(selectedRows)}
                                    >
                                        ADD
                                    </Button>
                                    <Button
                                        style={{ color: '#3611c8' }}
                                        iconType="fa-solid fa-rotate"
                                        type="button"
                                        styleType="link"
                                        onClick={onUpdateProducts}
                                    >
                                        UPDATE
                                    </Button>
                                    <Button
                                        style={{ color: '#3611c8' }}
                                        iconType="fa-solid fa-eye-slash"
                                        type="button"
                                        styleType="link"
                                        onClick={() => onBulkChangeIgnoreFlag(selectedRows)}
                                    >
                                        IGNORE
                                    </Button>
                                    <Button
                                        style={{ color: '#3611c8' }}
                                        iconType="fa-solid fa-eye"
                                        type="button"
                                        styleType="link"
                                        onClick={() => onBulkChangeIgnoreFlag(selectedRows)}
                                    >
                                        UNIGNORE
                                    </Button>
                                </>
                            )}
                        </div>
                    </ContentBlock>
                </Banner>
            )}
            <JobStatusDrawer />
            <DataGrid
                className={styles.dataGrid}
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: productUpdates,
                    headerHeight: 45,
                    onSelectionChanged,
                    isRowSelectable: (params) => {
                        const action = params.data?.action;
                        const isActionValid = action === 'ADD' || action === 'UPDATE';
                        // Permission check: user must be able to perform at least one action
                        const abilityToMoveToAndFromESPN = isRLProductGateAdmin || isRLProductGateEspnPlusUser;
                        const hasAddIgnoreControls = espnTitle ? isRLProductGateAdmin : isRLProductGateUser;
                        return isActionValid && (abilityToMoveToAndFromESPN || hasAddIgnoreControls);
                    },
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                selectable={
                    espnTitle
                        ? (isRLProductGateAdmin || isRLProductGateEspnPlusUser)
                        : (isRLProductGateAdmin || isRLProductGateUser || isRLProductGateEspnPlusUser)
                }
                selectMode={'multiple'}
                noResultsMessage={showSpinner ? 'Loading...' : 'No Products Updates'}
            />
        </>
    );
};
